import { apiService } from './api';
import { 
  ApiResponse, 
  Souscription, 
  Echeancier, 
  Paiement, 
  Notification,
  Projet,
  BatimentConfigurationPublic,
  DashboardStats,
  Document as ApiDocument
} from '../types/api';

class ClientApiService {
  private api = apiService;

  // Méthodes pour les statistiques client (filtrées par l'utilisateur connecté)
  async getClientDashboardStats(): Promise<any> {
    // Utiliser le nouvel endpoint spécialisé pour les statistiques client
    const response = await this.api['api'].get('/client-dashboard/stats/');
    return response.data;
  }

  // Méthodes pour récupérer les données du client connecté
  async getMySouscriptions(params: any = {}): Promise<ApiResponse<Souscription>> {
    return this.api.getSouscriptions(params);
  }

  // Méthode pour récupérer les mises à jour de projet du client
  async getMyProjectUpdates(): Promise<any[]> {
    const response = await this.api['api'].get('/client-dashboard/project_updates/');
    return response.data;
  }

  async getMyEcheanciers(params: any = {}): Promise<ApiResponse<Echeancier> | Echeancier[]> {
    return this.api.getEcheanciers(params);
  }

  async getMyPaiements(params: any = {}): Promise<ApiResponse<Paiement>> {
    return this.api.getPaiements(params);
  }

  async telechargerFacture(id: number): Promise<Blob> {
    return this.api.telechargerFacture(id);
  }

  async getMyNotifications(params: any = {}): Promise<ApiResponse<Notification>> {
    return this.api.getNotifications(params);
  }

  async getMyProfile(): Promise<any> {
    const user = this.api.getCurrentUser();
    if (!user) {
      throw new Error('Utilisateur non connecté');
    }
    return this.api.getUser(user.id);
  }

  async updateMyProfile(userId: number, data: any): Promise<any> {
    return this.api.updateUserProfile(userId, data);
  }

  // Méthodes pour créer de nouvelles souscriptions
  async createSouscription(data: Partial<Souscription>): Promise<Souscription> {
    return this.api.createSouscription(data);
  }

  // Méthodes pour les paiements
  async createPaiement(data: Partial<Paiement>): Promise<Paiement> {
    return this.api.createPaiement(data);
  }

  async createMultiSubscriptionPaiement(data: {
    montant_total_paye: number;
    date_paiement: string;
    mode_paiement: string;
    reference_paiement?: string;
    commentaire?: string;
    repartitions: Array<{
      souscription_id: number;
      montant_alloue: number;
    }>;
  }): Promise<Paiement> {
    return this.api.createMultiSubscriptionPaiement(data);
  }

  async uploadDocument(file: File, type: string, description: string): Promise<any> {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('type_document', type);
    formData.append('description', description);
    
    // Cette méthode devra être implémentée côté API
    return this.api['api'].post('/documents/upload/', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
  }

  // Méthodes pour récupérer les documents du client
  async getMyDocuments(params: any = {}): Promise<ApiResponse<ApiDocument>> {
    return this.api.getDocuments(params);
  }

  // Méthodes pour récupérer les factures du client
  async getMyFactures(): Promise<any> {
    return this.api['api'].get('/client-dashboard/factures/').then(res => res.data);
  }

  // Méthodes pour uploader un document d'identité
  async uploadDocumentIdentite(file: File, nomDocument: string, description: string = ''): Promise<any> {
    const formData = new FormData();
    formData.append('fichier', file);
    formData.append('nom_document', nomDocument);
    formData.append('description', description);

    return this.api['api'].post('/documents/upload_identite/', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    }).then(res => res.data);
  }

  // Méthodes pour supprimer un document
  async deleteDocument(documentId: number): Promise<void> {
    return this.api['api'].delete(`/documents/${documentId}/`);
  }

  // Méthodes pour marquer les notifications comme lues
  async markNotificationAsRead(notificationId: number): Promise<Notification> {
    return this.api.marquerNotificationLu(notificationId);
  }

  async markAllNotificationsAsRead(): Promise<void> {
    return this.api.marquerToutesNotificationsLues();
  }

  // Méthodes pour récupérer les projets disponibles (pour souscription)
  async getAvailableProjects(): Promise<ApiResponse<Projet>> {
    return this.api.getProjets({ statut_projet: 'EN_COURS' });
  }

  async getAvailableTypeMaisons(projetId: number): Promise<BatimentConfigurationPublic[]> {
    return this.api.getProjetMaisonsDisponibles(projetId);
  }

  // Méthodes pour générer des rapports personnalisés
  async getMyPaymentHistory(params: any = {}): Promise<any> {
    const paiements = await this.api.getPaiements(params);
    return paiements.results.map(paiement => ({
      id: paiement.id,
      date: paiement.date_paiement,
      montant: paiement.montant_total_paye || paiement.montant_paye,
      statut: paiement.statut_paiement,
      mode: paiement.mode_paiement,
      reference: paiement.reference_paiement,
      souscription: paiement.souscription,
    }));
  }

  async getMyEcheancierHistory(params: any = {}): Promise<any> {
    const echeanciers = await this.api.getEcheanciers(params);
    
    // Gérer les deux cas : avec et sans pagination
    const echeanciersList = Array.isArray(echeanciers) ? echeanciers : echeanciers.results;
    
    return echeanciersList.map((echeance: any) => ({
      id: echeance.id,
      numero: echeance.numero_echeance,
      date: echeance.date_echeance,
      montant: echeance.montant_prevu,
      statut: echeance.statut_echeance,
      souscription: echeance.souscription,
    }));
  }

  // Méthodes pour la gestion du profil utilisateur
  async changeMyPassword(userId: number, passwordData: { current_password: string; new_password: string; confirm_password: string }): Promise<void> {
    return this.api.changeUserPassword(userId, passwordData);
  }
}

// Export d'une instance singleton
export const clientApiService = new ClientApiService();
export default clientApiService;
